import React, { useMemo } from 'react'
import { ProductListItem } from './productListItem'
import { ProductPricingPlans } from './productPricingPlans'
import { ProductListItemProduct } from './types'
import './productsList.scss'
import { useGetArticlesWithPricingPlansQuery } from 'memberships/reduxQueries'
import { getProductName } from 'products/productTable/utils'
import { extractContentFromHTML } from 'products/crud/utils'
import { formatRenewal, getYearlyPrice, isTwoYearPaymentPlan } from 'products/pricing/pricingPlans'
import { BaseRouteParams } from 'hocs'
import { useParams } from 'react-router-dom'
import { PageLoader } from 'uiComponents/loaders'

export const ProductsList = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { data: articlesWithPricingPlans, isLoading } = useGetArticlesWithPricingPlansQuery(accountSlug!)

    const products: ProductListItemProduct[] = useMemo(() => {
        if (!articlesWithPricingPlans?.items) return []

        return articlesWithPricingPlans.items
            .filter((item) => item.cohorts.length > 0)
            .map((item) => {
                const activeCohort = item.cohorts[0]
                const annualPlan = activeCohort.paymentPlans.find((p) => p.paymentInterval === 'P1Y')
                const monthlyPlan = activeCohort.paymentPlans.find((p) => p.paymentInterval === 'P1M')
                const upfrontPlan = activeCohort.paymentPlans.find((p) => p.paymentInterval === 'P2Y')
                const isTwoYearContract = activeCohort.paymentPlans.some(isTwoYearPaymentPlan)
                const renewalSourcePlan =
                    [annualPlan, upfrontPlan, monthlyPlan].find((p) => p?.automaticRenewal) ??
                    annualPlan ??
                    upfrontPlan ??
                    monthlyPlan

                return {
                    id: item.articleUuid,
                    name: item.articleName ? getProductName(item.articleName.text) as string : '',
                    description: item.articleDescription ? extractContentFromHTML(getProductName(item.articleDescription.text) as string) : '',
                    imageUrl: item.articleSquareImage || undefined,
                    pricingSummary: {
                        renewal: formatRenewal(renewalSourcePlan?.automaticRenewal, renewalSourcePlan),
                        contract: isTwoYearContract ? '2 years' : undefined,
                        annualTotal: annualPlan ? getYearlyPrice(annualPlan) : undefined,
                        upfrontTotal: upfrontPlan?.totalPrice,
                        monthlyTotal: monthlyPlan?.totalPrice,
                        downPayment: monthlyPlan?.initialPayment,
                        monthly: monthlyPlan?.installment,
                    },
                }
            })
    }, [articlesWithPricingPlans])

    if (isLoading) {
        return <PageLoader />
    }

    return (
        <div className="products-list">
            {products.map((product) => (
                <ProductListItem key={product.id} product={product}>
                    <ProductPricingPlans articleUuid={product.id} readOnly />
                </ProductListItem>
            ))}
        </div>
    )
}
